// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { ref, getDownloadURL, uploadBytesResumable, deleteObject } from 'firebase/storage';

function decodeURIComponentSafe(value) {
  /* Decode URI codes to regular characters. Catch errors to always return a valid string. */
  if (!value) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    return value.replaceAll('%20', ' ');
  }
}

export async function fetchImageUrl(storage, image_storage_path) {
  const imageStoragePathCleaned = decodeURIComponentSafe(image_storage_path);
  const imageRef = ref(storage, imageStoragePathCleaned);
  const url = getDownloadURL(imageRef).catch(() => {
    return '';
  });
  return url;
}

function getThumbnailPath(image_storage_path, thumb_extension = '_400x400') {
  /* Get the thumbnail storage path for the given image_storage_path */
  const fileExtension = image_storage_path.endsWith('.jpeg')
    ? '.jpeg'
    : image_storage_path.endsWith('.jpg')
      ? '.jpg'
      : image_storage_path.endsWith('.png')
        ? '.png'
        : '';

  return image_storage_path.replace(fileExtension, '') + thumb_extension + fileExtension;
}

export async function fetchImageThumbnailUrl(storage, image_storage_path, thumb_extension) {
  const thumbnailStoragePath = getThumbnailPath(image_storage_path, thumb_extension);
  const thumbImageRef = ref(storage, thumbnailStoragePath);
  const url = getDownloadURL(thumbImageRef).catch(() => {
    return fetchImageUrl(storage, image_storage_path);
  });
  return url;
}

function base64ToBlob(base64) {
  /** Convert base64 image to blob */
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: 'image/jpeg' });
}

export async function uploadImage(storage, image, storagePath) {
  /* Upload an image to storage to the given storagePath (including filename) */
  const imageDataBlob = base64ToBlob(image);
  const storageRef = ref(storage, storagePath);
  await uploadBytesResumable(storageRef, imageDataBlob);
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
}

export async function uploadFileToFirebaseStorage(storage, file, storagePath) {
  /* Upload an image to storage to the given storagePath (including filename) */
  const storageRef = ref(storage, storagePath);
  const metadata = { contentType: file.type };
  await uploadBytesResumable(storageRef, file, metadata);
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
}

export async function loadImageData(imageUrl) {
  /* Fetch image data from the given imageUrl */
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      resolve(img);
    };
    img.onerror = (error) => {
      reject(new Error(`Failed to load image at ${imageUrl}: ${error.message}`));
    };
  });
}

export async function downloadImage(url, filename) {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }, 100);
  } catch (error) {
    console.error('Error downloading image:', error);
    // Fallback method: open image in new tab if direct download fails
    window.open(url, '_blank');
  }
}

export async function authAndDownloadImage(storage, image_storage_path, filename) {
  try {
    const url = await fetchImageUrl(storage, image_storage_path);
    if (!url) {
      throw new Error('Failed to retrieve download URL from Firebase');
    }

    await downloadImage(url, filename);
  } catch (error) {
    console.error('Error in authAndDownloadImage:', error);
    alert('Failed to download image. Please try again later.');
  }
}

export async function deleteImageFromStorage(storage, image_storage_path, thumb_extensions = ['_400x400']) {
  /* Delete an image (and associated thumbnail) from storage */
  const imageRef = ref(storage, image_storage_path);
  deleteObject(imageRef).catch((error) => {
    console.error(`Failed to delete image for ${image_storage_path}: ${error.message}`);
  });

  // Delete thumbnails
  if (!thumb_extensions) return;
  for (const thumb_extension of thumb_extensions) {
    const thumbnailUrl = getThumbnailPath(image_storage_path, thumb_extension);
    const thumbImageRef = ref(storage, thumbnailUrl);
    deleteObject(thumbImageRef).catch((error) => {
      console.error(`Failed to delete thumbnail for ${image_storage_path}: ${error.message}`);
    });
  }
}

export function rotateImageWithCanvas(imageElement, rotationDegrees) {
  /* Rotate image data with canvas. Returns data URL. */
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const rotationRadians = (rotationDegrees * Math.PI) / 180;

  imageElement.crossOrigin = 'anonymous';

  // For 90° and 270° rotations, swap width and height
  if (Math.abs(rotationDegrees) % 180 === 90) {
    canvas.width = imageElement.naturalHeight;
    canvas.height = imageElement.naturalWidth;
  } else {
    canvas.width = imageElement.naturalWidth;
    canvas.height = imageElement.naturalHeight;
  }

  // Move to center and rotate
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(rotationRadians);

  // Draw image centered
  ctx.drawImage(
    imageElement,
    -imageElement.naturalWidth / 2,
    -imageElement.naturalHeight / 2,
    imageElement.naturalWidth,
    imageElement.naturalHeight,
  );

  return canvas.toDataURL('image/jpeg', 0.9);
}
